/**
 * Hook for fetching time slots from /api/search-all-hours endpoint
 * Fetches slots for all service types in parallel
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@core/api/apiClient';
import type { ServiceType } from '../state/types';
import type {
  TimeSlotsResponse,
  SlotsByService,
  ProcessedSlot,
  ProcessedSlotsByService,
  ServiceWithSlots,
} from '../types/timeSlots';
import { buildMenuAreaSegments } from '../utils/menuAreaSegments';

/**
 * Query key factory for time slots
 */
export const timeSlotsKeys = {
  all: ['timeSlots'] as const,
  byService: (
    restaurantId: number,
    partySize: number,
    date: string,
    serviceType: ServiceType,
    lang: string,
    menuFilter?: number[] | null,
    areaFilter?: number[] | null,
  ) => {
    const base = [...timeSlotsKeys.all, restaurantId, partySize, date, serviceType, lang] as const;
    const menuKey = menuFilter?.length ? menuFilter.slice().sort().join('-') : '';
    const areaKey = areaFilter?.length ? `a:${areaFilter.slice().sort().join('-')}` : '';
    if (!menuKey && !areaKey) return base;
    if (menuKey && !areaKey) return [...base, menuKey] as const;
    if (!menuKey && areaKey) return [...base, areaKey] as const;
    return [...base, menuKey, areaKey] as const;
  },
};

/**
 * Fetch time slots for a specific service type.
 * URL composition mirrors the backend contract (see buildMenuAreaSegments for
 * the menu/area segment details):
 *   marketplace menu  → /{meitreNetworkMenuId} (overrides menuFilter)
 */
export async function fetchTimeSlots(
  lang: string,
  restaurantId: number,
  partySize: number,
  date: string,
  serviceType: ServiceType,
  menuFilter?: number[] | null,
  meitreNetworkMenuId?: number | null,
  areaFilter?: number[] | null,
): Promise<TimeSlotsResponse & { serviceType: ServiceType }> {
  let url = `/api/search-all-hours/${lang}/${partySize}/${date}/${serviceType}/${restaurantId}`;
  if (meitreNetworkMenuId) {
    // Marketplace flow pins the menu; AREA_FILTER is not exposed in this path.
    url += `/${meitreNetworkMenuId}`;
  } else {
    url += buildMenuAreaSegments(menuFilter, areaFilter);
  }

  const response = await api.get<TimeSlotsResponse>(url);

  return {
    ...response,
    serviceType,
  };
}

/**
 * Filter and process slots from raw API response.
 * Removes unavailable slots (type === 'No') and preferred slots if user doesn't have access.
 * When NOT in marketplace flow, filters out menus with marketplace: true from each slot.
 */
export function processSlots(
  slots: TimeSlotsResponse['center']['slots'],
  hasPreferredAccess: boolean = false,
  isMeitreNetworkFlow: boolean = false,
): ProcessedSlot[] {
  return slots
    .filter((slot) => {
      // Remove unavailable slots
      if (slot.type === 'No') return false;
      // Remove preferred slots if user doesn't have access
      if (slot.preferred && !hasPreferredAccess) return false;
      return true;
    })
    .map((slot) => {
      // Filter out deleted menus (backend may still include them) and,
      // when NOT in marketplace flow, marketplace-only menus.
      const filteredMenus = slot.menus.filter(
        (m) => !m.isDeleted && (isMeitreNetworkFlow || !m.marketplace),
      );

      // Sort: event menus first, then by orderPriority (matches legacy)
      const menus = [...filteredMenus].sort((a, b) => {
        if (a.eventMenu && !b.eventMenu) return -1;
        if (!a.eventMenu && b.eventMenu) return 1;
        return (a.orderPriority ?? 0) - (b.orderPriority ?? 0);
      });

      const sortedAreas = slot.availableAreas
        ? [...slot.availableAreas].sort((a, b) => a.priority - b.priority)
        : undefined;

      return {
        hour: slot.hour,
        available: true,
        preferred: slot.preferred,
        menus,
        deposit: slot.deposit,
        type: slot.type,
        fee: slot.fee,
        feeUSD: slot.feeUSD,
        feeKids: slot.feeKids,
        feeKidsUSD: slot.feeKidsUSD,
        guaranteeType: slot.guaranteeType,
        allFees: slot.allFees,
        specialRequestGuaranties: slot.specialRequestGuaranties,
        specialRequestGuarantiesType: slot.specialRequestGuarantiesType,
        specialRequestGuarantiesAmount: slot.specialRequestGuarantiesAmount,
        availableAreas: sortedAreas,
      };
    });
}

/**
 * Fallback service type display order (by typical meal time)
 */
const SERVICE_ORDER: ServiceType[] = ['breakfast', 'brunch', 'lunch', 'dinner'];

/**
 * Build the per-service {@link ServiceWithSlots} entries from the raw and processed
 * slot maps. Exported for unit tests; the hook composes this with {@link sortServicesByOrder}.
 */
export function buildServicesWithSlots(
  serviceTypes: ServiceType[],
  slotsByService: SlotsByService,
  processedSlots: ProcessedSlotsByService,
): ServiceWithSlots[] {
  return serviceTypes.map((service) => {
    const slots = processedSlots[service] || [];
    const rawSlotCount = slotsByService[service]?.center?.slots?.length ?? 0;
    const firstSlot = slots.length > 0 ? slots[0].hour : null;
    const lastSlot = slots.length > 0 ? slots[slots.length - 1].hour : null;
    return {
      service,
      slots,
      firstSlot,
      lastSlot,
      hasAvailability: slots.length > 0,
      hasRawSlots: rawSlotCount > 0,
    };
  });
}

/**
 * Sort services by their first available slot hour.
 * Falls back to the fixed SERVICE_ORDER when slots are not yet loaded.
 */
function sortServicesByOrder(services: ServiceWithSlots[]): ServiceWithSlots[] {
  const hasSlotHours = services.some((s) => s.firstSlot);

  if (!hasSlotHours) {
    return [...services].sort((a, b) => {
      return SERVICE_ORDER.indexOf(a.service) - SERVICE_ORDER.indexOf(b.service);
    });
  }

  return [...services].sort((a, b) => {
    if (!a.firstSlot && !b.firstSlot) {
      return SERVICE_ORDER.indexOf(a.service) - SERVICE_ORDER.indexOf(b.service);
    }
    if (!a.firstSlot) return 1;
    if (!b.firstSlot) return -1;
    return a.firstSlot.localeCompare(b.firstSlot);
  });
}

interface UseTimeSlotsOptions {
  /** Restaurant ID */
  restaurantId: number | undefined;
  /** Party size (number of diners) */
  partySize: number;
  /** Selected date in YYYY-MM-DD format */
  date: string | null;
  /** Service types to fetch */
  serviceTypes: ServiceType[];
  /** Whether to enable the queries */
  enabled?: boolean;
  /** Whether user has preferred access */
  hasPreferredAccess?: boolean;
  /** Meitre Network marketplace menu ID — when set, appends to search-all-hours URL */
  meitreNetworkMenuId?: number | null;
  /** Area filter IDs (Flow D) — when set, restricts the search to the given areas. */
  areaFilterIds?: number[] | null;
}

interface UseTimeSlotsResult {
  /** Raw slots data by service */
  slotsByService: SlotsByService;
  /** Processed (filtered) slots by service */
  processedSlots: ProcessedSlotsByService;
  /** Services with their slot information, sorted by first available slot hour */
  servicesWithSlots: ServiceWithSlots[];
  /** Whether any query is loading */
  isLoading: boolean;
  /** Whether all queries have finished */
  isSettled: boolean;
  /** Combined error from any failed query */
  error: Error | null;
  /** Refetch all time slots */
  refetch: () => void;
  /** Get processed slots for a specific service */
  getServiceSlots: (service: ServiceType) => ProcessedSlot[];
  /** Check if a service has available slots */
  hasAvailableSlots: (service: ServiceType) => boolean;
  /** Get first and last slot hours for a service */
  getServiceTimeRange: (service: ServiceType) => { first: string; last: string } | null;
}

/**
 * Hook to fetch and manage time slots for all service types
 *
 * @example
 * ```tsx
 * const { processedSlots, isLoading, servicesWithSlots } = useTimeSlots({
 *   restaurantId: restaurant.id,
 *   partySize: diners,
 *   date: '2026-01-26',
 *   serviceTypes: ['lunch', 'dinner'],
 *   enabled: true,
 * });
 *
 * // Get slots for dinner
 * const dinnerSlots = getServiceSlots('dinner');
 * ```
 */
export function useTimeSlots({
  restaurantId,
  partySize,
  date,
  serviceTypes,
  enabled = true,
  hasPreferredAccess = false,
  meitreNetworkMenuId,
  areaFilterIds,
}: UseTimeSlotsOptions): UseTimeSlotsResult {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const lang = i18n.language || 'en';
  const isMeitreNetworkFlow = !!meitreNetworkMenuId;

  // Create parallel queries for each service type
  const queries = useQueries({
    queries: serviceTypes.map((serviceType) => ({
      queryKey: timeSlotsKeys.byService(
        restaurantId!,
        partySize,
        date!,
        serviceType,
        lang,
        meitreNetworkMenuId ? [meitreNetworkMenuId] : undefined,
        meitreNetworkMenuId ? undefined : (areaFilterIds ?? undefined),
      ),
      queryFn: () =>
        fetchTimeSlots(lang, restaurantId!, partySize, date!, serviceType, undefined, meitreNetworkMenuId, areaFilterIds),
      enabled: enabled && !!restaurantId && partySize > 0 && !!date,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    })),
  });

  // Aggregate raw results
  const slotsByService = useMemo(() => {
    const result: SlotsByService = {};
    queries.forEach((q) => {
      if (q.isSuccess && q.data) {
        const data = q.data as TimeSlotsResponse & { serviceType: ServiceType };
        result[data.serviceType] = data;
      }
    });
    return result;
  }, [queries]);

  // Process and filter slots
  const processedSlots = useMemo(() => {
    const result: ProcessedSlotsByService = {};
    Object.entries(slotsByService).forEach(([service, response]) => {
      result[service] = processSlots(response.center.slots, hasPreferredAccess, isMeitreNetworkFlow);
    });
    return result;
  }, [slotsByService, hasPreferredAccess, isMeitreNetworkFlow]);

  // Build services with slots array
  const servicesWithSlots = useMemo(() => {
    return sortServicesByOrder(
      buildServicesWithSlots(serviceTypes, slotsByService, processedSlots),
    );
  }, [serviceTypes, processedSlots, slotsByService]);

  // Derived loading state
  const isLoading = queries.some((q) => q.isLoading);
  const isSettled = queries.every((q) => q.isSuccess || q.isError);

  // Get first error if any
  const error = useMemo(() => {
    const failedQuery = queries.find((q) => q.isError);
    return failedQuery?.error as Error | null;
  }, [queries]);

  // Refetch all queries
  const refetch = useCallback(() => {
    queries.forEach((q) => q.refetch());
  }, [queries]);

  // Helper: Get slots for a specific service
  const getServiceSlots = useCallback(
    (service: ServiceType): ProcessedSlot[] => {
      return processedSlots[service] || [];
    },
    [processedSlots]
  );

  // Helper: Check if service has available slots
  const hasAvailableSlots = useCallback(
    (service: ServiceType): boolean => {
      const slots = processedSlots[service];
      return slots ? slots.length > 0 : false;
    },
    [processedSlots]
  );

  // Helper: Get time range for a service
  const getServiceTimeRange = useCallback(
    (service: ServiceType): { first: string; last: string } | null => {
      const slots = processedSlots[service];
      if (!slots || slots.length === 0) return null;
      return {
        first: slots[0].hour,
        last: slots[slots.length - 1].hour,
      };
    },
    [processedSlots]
  );

  return {
    slotsByService,
    processedSlots,
    servicesWithSlots,
    isLoading,
    isSettled,
    error,
    refetch,
    getServiceSlots,
    hasAvailableSlots,
    getServiceTimeRange,
  };
}
